import React, { useState, useEffect } from 'react';
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  TextField,
  Button,
  Typography,
  CircularProgress,
  Alert,
  Box,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  Chip,
  IconButton
} from '@mui/material';
import { Settings } from '@mui/icons-material';
import { useTranslation } from 'react-i18next';
import { useAppContext } from '../context/AppContext';
import AIKeysManager from './AIKeysManager';
import { calculatePAC, calculatePOD } from '../utils/ingredientCalculations';

const AI_PROVIDERS = {
  GEMINI: 'gemini',
  OPENAI: 'openai',
  CLAUDE: 'claude',
  OLLAMA: 'ollama'
};

const AIIngredientCreator = ({ open, onClose, onImport }) => {
  const { t } = useTranslation();
  const { api } = useAppContext();
  const [description, setDescription] = useState('');
  const [selectedProvider, setSelectedProvider] = useState(AI_PROVIDERS.GEMINI);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const [generatedIngredient, setGeneratedIngredient] = useState(null);
  const [usedProvider, setUsedProvider] = useState('');
  const [showKeysManager, setShowKeysManager] = useState(false);
  const [keysStatus, setKeysStatus] = useState({
    gemini: false,
    openai: false,
    claude: false,
    ollama_url: '',
  });

  useEffect(() => {
    if (open) {
      loadKeysStatus();
    }
  }, [open]);

  const loadKeysStatus = async () => {
    try {
      const data = await api.getAIKeysStatus();
      setKeysStatus(data.ai_keys);
    } catch (error) {
      console.error('Error loading keys status:', error);
    }
  };

  const getAvailableProviders = () => {
    const providers = [];
    
    // Gemini siempre está disponible (usa key por defecto si el usuario no tiene una)
    providers.push({ value: AI_PROVIDERS.GEMINI, label: 'Google Gemini' });
    
    if (keysStatus.openai) {
      providers.push({ value: AI_PROVIDERS.OPENAI, label: 'OpenAI GPT' });
    }
    if (keysStatus.claude) {
      providers.push({ value: AI_PROVIDERS.CLAUDE, label: 'Anthropic Claude' });
    }
    if (keysStatus.ollama_url) {
      providers.push({ value: AI_PROVIDERS.OLLAMA, label: 'Ollama (Local)' });
    }
    
    return providers;
  };

  const generateIngredient = async () => {
    if (!description.trim()) return;

    setLoading(true);
    setError('');
    setGeneratedIngredient(null);

    try {
      const data = await api.generateIngredient(description.trim(), selectedProvider);

      if (data.success) {
        // Convert backend format to frontend format
        const ingredient = {
          ...data.ingredient,
          // Ensure compatibility with existing ingredient structure
          name: data.ingredient.name,
          category: data.ingredient.category_id || 1,
          sugars: data.ingredient.sugars || 0,
          fats: data.ingredient.fats || 0,
          milkfat: data.ingredient.milkfat || 0,
          proteins: data.ingredient.proteins || 0,
          other_solids: data.ingredient.other_solids || 0,
          total_solids: data.ingredient.total_solids || 0,
          water: data.ingredient.water || 0,
          pac: calculatePAC(data.ingredient),
          pod: calculatePOD(data.ingredient),
          msnf: data.ingredient.msnf || 0,
          alcohol: data.ingredient.alcohol || 0,
          salt: data.ingredient.salt || 0,
          stabilizer: data.ingredient.stabilizer || 0,
          emulsifiers: data.ingredient.emulsifiers || 0,
          hardening_factor: data.ingredient.hardening_factor || 0,
          translations: {
            es: data.ingredient.translations.es || '',
            it: data.ingredient.translations.it || '',
            fr: data.ingredient.translations.fr || ''
          },
          can_be_mix: data.ingredient.can_be_mix || true,
          can_be_inclusion: data.ingredient.can_be_inclusion || true,
        };
        let total_solids = ingredient.sugars + ingredient.fats + ingredient.proteins + ingredient.other_solids + ingredient.salt;
        let diff_solids = total_solids - ingredient.total_solids < ingredient.other_solids;
        // Si puedo corrijo con other_solids
        if (diff_solids) {
          ingredient.other_solids = Math.max(0, ingredient.other_solids - (total_solids - ingredient.total_solids));
          total_solids = ingredient.sugars + ingredient.fats + ingredient.proteins + ingredient.other_solids + ingredient.salt;
        }

        ingredient.total_solids = Math.min(100, total_solids);
        ingredient.water = Math.max(0, 100 - total_solids - ingredient.alcohol);
        setGeneratedIngredient(ingredient);
        setUsedProvider(data.provider_used);
        setError('');
      } else {
        setError(data.error || t('aiIngredient.generationError'));
      }
    } catch (error) {
      console.error('Error generating ingredient:', error);
      setError(t('aiIngredient.networkError'));
    } finally {
      setLoading(false);
    }
  };

  const handleImport = () => {
    if (generatedIngredient) {
      onImport(generatedIngredient);
      handleClose();
    }
  };

  const handleClose = () => {
    setDescription('');
    setGeneratedIngredient(null);
    setError('');
    setUsedProvider('');
    onClose();
  };

  const availableProviders = getAvailableProviders();
  const hasProviders = availableProviders.length > 0;

  return (
    <>
      <Dialog open={open} onClose={handleClose} maxWidth="md" fullWidth>
        <DialogTitle>
          <Box display="flex" justifyContent="space-between" alignItems="center">
            {t('aiIngredient.title')}
            <IconButton 
              onClick={() => setShowKeysManager(true)}
              title={t('aiIngredient.manageKeys')}
            >
              <Settings />
            </IconButton>
          </Box>
        </DialogTitle>
        
        <DialogContent>
          {!hasProviders && (
            <Alert severity="warning" sx={{ mb: 2 }}>
              {t('aiIngredient.noKeysConfigured')}
              <Button 
                onClick={() => setShowKeysManager(true)}
                sx={{ ml: 1 }}
              >
                {t('aiIngredient.configureKeys')}
              </Button>
            </Alert>
          )}

          <TextField
            fullWidth
            multiline
            rows={3}
            label={t('aiIngredient.description')}
            placeholder={t('aiIngredient.placeholder')}
            value={description}
            onChange={(e) => setDescription(e.target.value)}
            margin="normal"
            disabled={!hasProviders}
          />

          {hasProviders && (
            <FormControl fullWidth margin="normal">
              <InputLabel>{t('aiIngredient.provider')}</InputLabel>
              <Select
                value={selectedProvider}
                onChange={(e) => setSelectedProvider(e.target.value)}
                label={t('aiIngredient.provider')}
              >
                {availableProviders.map((provider) => (
                  <MenuItem key={provider.value} value={provider.value}>
                    {provider.label}
                  </MenuItem>
                ))}
              </Select>
            </FormControl>
          )}

          {error && (
            <Alert severity="error" sx={{ mt: 2 }}>
              {error}
            </Alert>
          )}

          {generatedIngredient && (
            <Box sx={{ mt: 2, p: 2, bgcolor: 'background.paper', border: 1, borderColor: 'divider', borderRadius: 1 }}>
              <Box display="flex" alignItems="center" gap={1} mb={1}>
                <Typography variant="h6">{t('aiIngredient.generated')}</Typography>
                <Chip 
                  label={`${t('aiIngredient.via')} ${availableProviders.find(p => p.value === usedProvider)?.label || usedProvider}`} 
                  size="small" 
                  color="primary" 
                />
              </Box>
              
              <Typography variant="body1" fontWeight="bold">{generatedIngredient.name}</Typography>
              <Typography variant="body2" color="textSecondary">{generatedIngredient.notes}</Typography>
              
              <Box display="flex" flexWrap="wrap" gap={1} mt={1}>
                <Chip label={`${t('sugars')}: ${generatedIngredient.sugars?.toFixed(1)}%`} size="small" />
                <Chip label={`${t('fats')}: ${generatedIngredient.fats?.toFixed(1)}%`} size="small" />
                <Chip label={`${t('proteins')}: ${generatedIngredient.proteins?.toFixed(1)}%`} size="small" />
                <Chip label={`${t('water')}: ${generatedIngredient.water?.toFixed(1)}%`} size="small" />
                <Chip label={`${t('PAC')}: ${generatedIngredient.pac?.toFixed(1)}`} size="small" />
                <Chip label={`${t('POD')}: ${generatedIngredient.pod?.toFixed(1)}`} size="small" />
              </Box>
            </Box>
          )}
        </DialogContent>

        <DialogActions>
          <Button onClick={handleClose}>{t('cancel')}</Button>
          <Button 
            onClick={generateIngredient} 
            disabled={!description.trim() || loading || !hasProviders}
            variant="outlined"
          >
            {loading ? <CircularProgress size={20} /> : t('aiIngredient.generate')}
          </Button>
          {generatedIngredient && (
            <Button onClick={handleImport} variant="contained">
              {t('aiIngredient.import')}
            </Button>
          )}
        </DialogActions>
      </Dialog>

      <AIKeysManager 
        open={showKeysManager}
        onClose={() => setShowKeysManager(false)}
        onKeysUpdated={loadKeysStatus}
      />
    </>
  );
};

export default AIIngredientCreator;
